import {
    alpha,
    Box,
    Button,
    darken,
    Grid,
    Stack,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { useLocale } from '@repo/i18n';
import { ProductInstance } from '@repo/types';
import { bookingWidgetInputStyle } from 'src/components/utils/jss/TextFieldStyleUtils';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { getAvailableTimeSlots } from '@repo/widget-utils/DateHelpers';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';

import type { JSX } from 'react';

interface IProps {
    availabilityData: ProductInstance[] | undefined;
    selectedTimeSlot: TimeSlotType | undefined;
    onSelectTimeSlot(timeSlot: TimeSlotType): void;
    isTimeSlotsEqual?: (timeSlotA: TimeSlotType, timeSlotB: TimeSlotType) => boolean;
    hideHeader?: boolean;
    fullWidth?: boolean;
    color?: string;
    backgroundColor?: string;
    primaryColor?: string;
    primaryColorContrast?: string;
}

export default function InstancePicker(props: IProps): JSX.Element {
    const {
        isTimeSlotsEqual = (a: TimeSlotType, b: TimeSlotType) => a.product.id === b.product.id,
    } = props;
    const { t } = useLocale();
    const customizations = useCustomizations();
    const theme = useTheme();

    const {
        selectedTimeSlot,
        onSelectTimeSlot,
        availabilityData,
        color = theme.palette.common.black,
        backgroundColor = theme.palette.common.white,
        primaryColor = customizations.bookingWidgetColor,
        primaryColorContrast = customizations.bookingWidgetColorContrast,
    } = props;

    const waitingListProducts =
        availabilityData?.filter((p) => p.hasWaitingList && p.isClosedForBooking) ?? [];
    const waitingListProductIds = waitingListProducts.map((p) => p.id);

    // Regular products are:
    // - not waiting list products
    const regularProducts =
        availabilityData?.filter((p) => !waitingListProductIds.includes(p.id)) ?? [];

    const regularSlots = getAvailableTimeSlots(regularProducts);

    const waitingListSlots = waitingListProducts.map((product) => ({
        label: product.start.format('HH:mm'),
        sublabel: t.waiting_list,
        product: { ...product },
        isWaitingList: true,
    }));

    const availableTimeSlots: (TimeSlotType & { isWaitingList?: boolean })[] = [
        ...regularSlots,
        ...waitingListSlots,
    ]
        .filter((slot) => slot.product.start?.isValid())
        .sort((a, b) => {
            const timeA = a.product.start?.valueOf() ?? 0;
            const timeB = b.product.start?.valueOf() ?? 0;
            return timeA - timeB;
        });

    const hasAvailableTickets = availableTimeSlots.length > 0;
    const colors = bookingWidgetInputStyle(customizations, backgroundColor, color);

    const timeSlots = availableTimeSlots.map((timeSlot) => {
        const isWaitingListSlot = timeSlot.isWaitingList;
        const isSelected = selectedTimeSlot && isTimeSlotsEqual(selectedTimeSlot, timeSlot);

        const isClosedForBooking = !isWaitingListSlot && timeSlot.product.isClosedForBooking;
        const isAtCapacity = !isWaitingListSlot && timeSlot.product.capacity === 0;
        const isPassedCutoff = timeSlot.product.start
            .subtract(timeSlot.product.cutoffTime ?? 0, 'minutes')
            .isBefore(Date.now());

        const slotColor = isWaitingListSlot ? theme.palette.warning.main : colors.color;
        const slotTextColor = isWaitingListSlot ? theme.palette.warning.contrastText : colors.color;

        return (
            <Grid
                key={`time-slot-${timeSlot.product.id}-${
                    timeSlot.product.product?.pkgProductId ?? ''
                }-${timeSlot.product.product?.pkgTicketOptionId ?? ''}`}
                component={Button}
                height="fit-content"
                border={`1px solid ${
                    isAtCapacity ? alpha(color, 0.5) : isSelected ? primaryColor : slotColor
                }`}
                borderRadius={`${theme.shape.borderRadius}px`}
                disabled={isAtCapacity || isPassedCutoff || isClosedForBooking}
                sx={{
                    px: theme.spacing(1.5),
                    py: timeSlot.sublabel ? theme.spacing(0.5) : theme.spacing(1.5),
                    backgroundColor: isWaitingListSlot
                        ? theme.palette.warning.light
                        : backgroundColor,
                    color: slotTextColor,
                    ...(isSelected && {
                        backgroundColor: primaryColor,
                        color: primaryColorContrast,
                        '&:hover': {
                            backgroundColor: (theme: Theme) =>
                                customizations.primaryButtonStyle === 'contained'
                                    ? darken(primaryColor, 0.2)
                                    : alpha(theme.palette.common.black, 0.3),
                        },
                    }),
                }}
                onClick={() => !isAtCapacity && !isClosedForBooking && onSelectTimeSlot(timeSlot)}
                aria-label={`${t.select_time} ${timeSlot.label} - ${timeSlot.sublabel}`}
                aria-pressed={!!isSelected}
            >
                <Box>
                    <Typography
                        sx={{
                            color: `${isSelected ? primaryColorContrast : slotTextColor} !important`,
                            ...((isAtCapacity || isClosedForBooking) && {
                                textDecoration: 'line-through',
                            }),
                        }}
                    >
                        {timeSlot.label}
                    </Typography>
                    {timeSlot.sublabel && (
                        <Typography
                            sx={{
                                mt: -0.5,
                                fontSize: '0.75rem',
                                color: `${
                                    isSelected ? primaryColorContrast : slotTextColor
                                } !important`,
                                ...((isAtCapacity || isClosedForBooking) && {
                                    textDecoration: 'none',
                                }),
                            }}
                        >
                            {isAtCapacity || isClosedForBooking ? t.full : timeSlot.sublabel}
                        </Typography>
                    )}
                </Box>
            </Grid>
        );
    });

    return (
        <Box
            sx={{
                padding: props.fullWidth ? undefined : theme.spacing(0, 1.5),
                mb: theme.spacing(1),
            }}
        >
            {!props.hideHeader && hasAvailableTickets && (
                <Grid container direction="row" alignItems="center">
                    <Typography sx={{ color: colors.color }}>
                        {capitalize(t.select_time)}
                    </Typography>
                    <Box
                        flex="1"
                        borderTop={`1px solid ${colors.color}`}
                        ml={theme.spacing(1)}
                        sx={{
                            opacity: 0.2,
                        }}
                    />
                </Grid>
            )}

            <Stack
                direction="row"
                padding={props.hideHeader ? 0 : theme.spacing(1, 0)}
                flexWrap="wrap"
                gap={theme.spacing(1)}
            >
                {hasAvailableTickets && timeSlots}
            </Stack>
        </Box>
    );
}
